import React, { useState, useEffect } from 'react';
import { X, Info, Server, Database, Globe, Clock, GitBranch } from 'lucide-react';

/**
 * Environment Info Panel
 * Shows environment details when ?env=1 is added to URL
 * Helps debug preview vs production mismatches
 */
const EnvironmentInfo = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [envInfo, setEnvInfo] = useState(null);
  const [apiHealth, setApiHealth] = useState(null);

  useEffect(() => {
    // Check if ?env=1 is in URL
    const params = new URLSearchParams(window.location.search);
    if (params.get('env') === '1') {
      setIsVisible(true);
      fetchEnvInfo();
    }
  }, []);

  const fetchEnvInfo = async () => {
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    
    // Build info from environment
    const info = {
      environment: detectEnvironment(),
      apiBaseUrl: apiUrl || 'NOT SET',
      buildTimestamp: process.env.REACT_APP_BUILD_TIMESTAMP || new Date().toISOString(),
      nodeEnv: process.env.NODE_ENV || 'unknown',
      currentUrl: window.location.origin,
      serviceWorker: 'serviceWorker' in navigator ? 'Supported' : 'Not Supported',
    };

    setEnvInfo(info);

    // Check API health
    try {
      const response = await fetch(`${apiUrl}/health`);
      const data = await response.json();
      setApiHealth({
        status: response.status,
        ...data
      });
      
      // Also fetch backend env info
      try {
        const envResponse = await fetch(`${apiUrl}/api/env-info`);
        const envData = await envResponse.json();
        setEnvInfo(prev => ({
          ...prev,
          backendEnv: envData.environment,
          databaseType: envData.database?.type,
          databaseName: envData.database?.name,
          databaseHost: envData.database?.host
        }));
      } catch (e) {
        console.log('Could not fetch backend env info');
      }
    } catch (error) {
      setApiHealth({
        status: 'ERROR',
        error: error.message
      });
    }
  };

  const detectEnvironment = () => {
    const url = window.location.hostname;
    if (url.includes('preview.emergentagent.com')) return 'PREVIEW';
    if (url.includes('localhost')) return 'LOCAL';
    if (url.includes('furd.co.uk')) return 'PRODUCTION';
    return 'UNKNOWN';
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[9999] max-w-md">
      <div className="bg-slate-900 text-white rounded-2xl shadow-2xl border border-slate-700 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 bg-slate-800 border-b border-slate-700">
          <div className="flex items-center gap-2">
            <Info className="w-5 h-5 text-blue-400" />
            <span className="font-bold">Environment Info</span>
          </div>
          <button 
            onClick={() => setIsVisible(false)}
            className="p-1 hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-3 text-sm">
          {envInfo && (
            <>
              {/* Environment Badge */}
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-gray-400" />
                <span className="text-gray-400">Environment:</span>
                <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                  envInfo.environment === 'PRODUCTION' ? 'bg-green-600' :
                  envInfo.environment === 'PREVIEW' ? 'bg-yellow-600' :
                  'bg-gray-600'
                }`}>
                  {envInfo.environment}
                </span>
              </div>

              {/* API URL */}
              <div className="flex items-start gap-2">
                <Server className="w-4 h-4 text-gray-400 mt-0.5" />
                <div>
                  <span className="text-gray-400">API Base URL:</span>
                  <div className="text-blue-400 break-all text-xs mt-1 font-mono bg-slate-800 p-2 rounded">
                    {envInfo.apiBaseUrl}
                  </div>
                </div>
              </div>

              {/* Current URL */}
              <div className="flex items-start gap-2">
                <Globe className="w-4 h-4 text-gray-400 mt-0.5" />
                <div>
                  <span className="text-gray-400">Current Origin:</span>
                  <div className="text-green-400 break-all text-xs mt-1 font-mono">
                    {envInfo.currentUrl}
                  </div>
                </div>
              </div>

              {/* Build Info */}
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-400" />
                <span className="text-gray-400">Build:</span>
                <span className="text-gray-300 text-xs">{envInfo.buildTimestamp}</span>
              </div>

              {/* Node Env */}
              <div className="flex items-center gap-2">
                <GitBranch className="w-4 h-4 text-gray-400" />
                <span className="text-gray-400">Node Env:</span>
                <span className="text-gray-300">{envInfo.nodeEnv}</span>
              </div>
            </>
          )}

          {/* API Health */}
          {apiHealth && (
            <div className="pt-3 border-t border-slate-700">
              <div className="flex items-center gap-2 mb-2">
                <Database className="w-4 h-4 text-gray-400" />
                <span className="text-gray-400">API Health:</span>
                <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                  apiHealth.status === 200 ? 'bg-green-600' : 'bg-red-600'
                }`}>
                  {apiHealth.status === 200 ? 'HEALTHY' : 'ERROR'}
                </span>
              </div>
              {apiHealth.database && (
                <div className="text-xs text-gray-400 ml-6">
                  Database: <span className={apiHealth.database === 'connected' ? 'text-green-400' : 'text-red-400'}>
                    {apiHealth.database}
                  </span>
                </div>
              )}
              {apiHealth.error && (
                <div className="text-xs text-red-400 ml-6 mt-1">
                  Error: {apiHealth.error}
                </div>
              )}
            </div>
          )}

          {/* Backend Environment */}
          {envInfo?.backendEnv && (
            <div className="pt-3 border-t border-slate-700">
              <div className="flex items-center gap-2 mb-2">
                <Server className="w-4 h-4 text-gray-400" />
                <span className="text-gray-400">Backend Environment:</span>
                <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                  envInfo.backendEnv === 'PRODUCTION' ? 'bg-green-600' : 'bg-yellow-600'
                }`}>
                  {envInfo.backendEnv}
                </span>
              </div>
              <div className="text-xs text-gray-400 ml-6 space-y-1">
                <div>DB Type: <span className="text-blue-400">{envInfo.databaseType}</span></div>
                <div>DB Name: <span className="text-blue-400">{envInfo.databaseName}</span></div>
                <div>DB Host: <span className="text-blue-400 font-mono text-[10px]">{envInfo.databaseHost}</span></div>
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="pt-3 border-t border-slate-700 text-xs text-gray-500">
            <p>Add <code className="bg-slate-800 px-1 rounded">?env=1</code> to any page URL to show this panel.</p>
            <p className="mt-1">Compare API Base URL between preview and production to diagnose mismatches.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnvironmentInfo;
